import { WORLD, MATCH_DURATION, NUM_BOTS, OBJECTS, OBJECT_KEYS, BOT_NAMES, VEHICLES, DEBRIS, comboMult } from "./config";
import { Vehicle, Obstacle, Resource, Particle, FloatText, rand } from "./entities";
import { initBot, updateBot } from "./bots";
import { Sound } from "./audio";
import { paintVehicle, paintObstacle } from "./render";

const CELL = 220;
const ZONES = [
  { x: 0, y: 0, w: 2100, h: 2100, tint: "rgba(255,149,0,0.05)", name: "CONSTRUCTION", weightLarge: 0.16 },
  { x: 2100, y: 0, w: 2100, h: 2100, tint: "rgba(0,229,255,0.05)", name: "DOWNTOWN", weightLarge: 0.06 },
  { x: 0, y: 2100, w: 2100, h: 2100, tint: "rgba(157,78,221,0.05)", name: "WAREHOUSE DISTRICT", weightLarge: 0.12 },
  { x: 2100, y: 2100, w: 2100, h: 2100, tint: "rgba(255,42,85,0.05)", name: "CONTAINER YARD", weightLarge: 0.10 },
];

export class GameEngine {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.input = opts.input;
    this.onHud = opts.onHud || (() => {});
    this.onEvent = opts.onEvent || (() => {});
    this.theme = opts.theme || "classic";
    this.playerName = opts.playerName || "YOU";
    this.quality = opts.quality || "high";
    this.matchDuration = opts.duration || MATCH_DURATION;
    this.cos = opts.cosmetics || {};
    this.multiplayer = !!opts.multiplayer;
    this.net = opts.net || null;
    this.seed = opts.seed || null;
    this._rng = this.seed ? mulberry32(this.seed) : Math.random;
    this.ghosts = new Map();
    this.mpStandings = [];
    this.mpTotal = 0;
    this.leaderPid = null;
    this.leaderRef = null;
    this.netAccum = 0;
    this.mpEnded = false;
    this.raf = null;
    this.last = 0;
    this.hudAccum = 0;
    this.shake = 0;
    this.running = false;
    this.ended = false;
    this._reset();
  }

  _reset() {
    this.obstacles = [];
    this.resources = [];
    this.particles = [];
    this.texts = [];
    this.shockwaves = [];
    this.grid = new Map();
    this.timeLeft = this.matchDuration;
    this._genWorld();
    this.player = new Vehicle(WORLD.w / 2, WORLD.h / 2, true, this.playerName, this.theme);
    this.player.trailColor = this.cos.trail || this.player.color;
    this._boostColor = this.cos.boost || this.cos.trail || this.player.color;
    this.vehicles = [this.player];
    if (!this.multiplayer) {
      for (let i = 0; i < NUM_BOTS; i++) {
        const a = (i / NUM_BOTS) * Math.PI * 2;
        const bx = WORLD.w / 2 + Math.cos(a) * 700, by = WORLD.h / 2 + Math.sin(a) * 700;
        const themes = ["classic", "fire", "neon", "chrome", "desert", "cyber"];
        const b = new Vehicle(bx, by, false, BOT_NAMES[i % BOT_NAMES.length], themes[i % themes.length]);
        const boost = Math.floor(rand(0, 4));
        for (let j = 0; j < boost; j++) b.addXp(200 * (j + 1));
        initBot(b);
        this.vehicles.push(b);
      }
    }
    this.cam = { x: this.player.x, y: this.player.y, zoom: 1 };
  }

  _genWorld() {
    const rng = this._rng;
    const count = this.quality === "low" ? 260 : 380;
    for (let i = 0; i < count; i++) {
      const zone = ZONES[Math.floor(rng() * ZONES.length)];
      const x = zone.x + 60 + rng() * (zone.w - 120);
      const y = zone.y + 60 + rng() * (zone.h - 120);
      if (Math.hypot(x - WORLD.w / 2, y - WORLD.h / 2) < 240) continue; // clear spawn
      const r = rng();
      let cat;
      if (r < zone.weightLarge) cat = "large";
      else if (r < zone.weightLarge + 0.28) cat = "medium";
      else cat = "small";
      const keys = OBJECT_KEYS.filter((k) => OBJECTS[k].cat === cat);
      const key = keys[Math.floor(rng() * keys.length)];
      const o = new Obstacle(x, y, key, OBJECTS[key]);
      this.obstacles.push(o);
    }
    // build spatial grid
    for (const o of this.obstacles) {
      o.cx = Math.floor(o.x / CELL); o.cy = Math.floor(o.y / CELL);
      const k = `${o.cx},${o.cy}`;
      if (!this.grid.has(k)) this.grid.set(k, []);
      this.grid.get(k).push(o);
    }
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    if (typeof window !== "undefined") window.__smash = this; // test/debug hook
    Sound.init(); Sound.resume();
    Sound.inMatch = true;
    if (Sound.musicEnabled) Sound.startMusic();
    if (this.multiplayer && this.net) {
      this.net.onSnap = (m) => this.applySnapshot(m);
      this.net.onDestroy = (m) => this.applyRemoteDestroy(m);
      this.net.onEnd = (m) => this._endMatchMP(m.standings);
      this.net.onKill = (m) => {
        if (m.victim && this.net.pid === m.victim) this._remoteKilledMe(m.by);
        else this.onEvent("kill", { name: m.by });
      };
    }
    this.raf = requestAnimationFrame((t) => this._loop(t));
  }
  stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    Sound.inMatch = false;
    Sound.stopMusic();
  }

  _loop(ts) {
    if (!this.running) return;
    let dt = (ts - this.last) / 1000;
    this.last = ts;
    if (dt > 0.05) dt = 0.05;
    const dtf = dt * 60;
    this._update(dt, dtf);
    this._render();
    this.hudAccum += dt;
    if (this.hudAccum > 0.08) { this.hudAccum = 0; this._pushHud(); }
    this.raf = requestAnimationFrame((t) => this._loop(t));
  }

  _update(dt, dtf) {
    if (this.ended) return;
    if (!this.multiplayer) {
      this.timeLeft -= dt;
      if (this.timeLeft <= 0) { this.timeLeft = 0; this._endMatch(); return; }
    }

    // player control
    if (this.player.alive && this.input) {
      const r = this.input.read();
      if (r.joystick) {
        this.player.control.targetAngle = Math.atan2(r.jy, r.jx);
        this.player.control.throttle = Math.min(1, Math.hypot(r.jx, r.jy));
        this.player.control.steer = 0;
      } else {
        this.player.control.targetAngle = null;
        this.player.control.throttle = r.throttle;
        this.player.control.steer = r.steer;
      }
      this.player.control.boost = r.boost;
      // Boost launch feedback: a kick of screen-shake + engine roar when it engages.
      const wantBoost = r.boost && this.player.boostEnergy > 1;
      if (wantBoost && !this._prevBoosting) { this.shake = Math.min(14, this.shake + 7); Sound.engine(1.4); }
      this._prevBoosting = wantBoost;
      if (Math.abs(this.player.speed) > 0.5) Sound.engine(Math.abs(this.player.speed) / 6);
    }

    // bots (single-player only)
    if (!this.multiplayer) {
      for (const v of this.vehicles) {
        if (v.isPlayer) continue;
        if (v.alive) updateBot(v, dt, WORLD, this.vehicles, this.grid, CELL);
        else if (v.respawnTimer <= 0) this._respawn(v);
      }
    }

    // movement
    for (const v of this.vehicles) v.update(dtf, dt, WORLD);

    // player respawn (suppressed while a watch-ad revive is pending)
    if (!this.player.alive && !this.awaitingRevive && this.player.respawnTimer <= 0) this._respawn(this.player);

    // collisions
    for (const v of this.vehicles) {
      if (!v.alive) continue;
      this._vehObstacles(v, dt, dtf);
    }
    if (!this.multiplayer) this._vehCombat(dt, dtf);
    if (!this.multiplayer) {
      let best = this.player;
      for (const v of this.vehicles) if (v.score > best.score) best = v;
      this.leaderRef = best;
    }

    // multiplayer: ghosts, combat vs ghosts, state streaming
    if (this.multiplayer) {
      for (const g of this.ghosts.values()) {
        g.x += (g.tx - g.x) * 0.25; g.y += (g.ty - g.y) * 0.25;
        let d = g.ta - g.angle; while (d > Math.PI) d -= 6.283; while (d < -Math.PI) d += 6.283;
        g.angle += d * 0.25;
      }
      this._ghostCombat();
      this.netAccum += dt;
      if (this.netAccum > 0.066 && this.net) {
        this.netAccum = 0;
        this.net.sendState({
          x: Math.round(this.player.x), y: Math.round(this.player.y),
          angle: +this.player.angle.toFixed(2), tier: this.player.tier,
          score: this.player.score, boosting: this.player.boosting,
          health: +(this.player.health / this.player.maxHealth).toFixed(2), alive: this.player.alive,
        });
      }
    }

    // resources (only player-affecting)
    for (const res of this.resources) {
      res.update(dtf, dt, this.player);
      if (res.dead === "collect") {
        this._collect(res);
      }
    }
    this.resources = this.resources.filter((r) => !r.dead);

    // particles / texts
    for (const p of this.particles) p.update(dtf, dt);
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const t of this.texts) t.update(dt);
    this.texts = this.texts.filter((t) => t.life > 0);
    for (const s of this.shockwaves) { s.life -= dt; s.r += (s.maxR - s.r) * 0.28; }
    this.shockwaves = this.shockwaves.filter((s) => s.life > 0);

    // camera
    const targetZoom = Math.max(0.55, Math.min(1.0, 20 / this.player.radius));
    this.cam.zoom += (targetZoom - this.cam.zoom) * 0.05;
    this.cam.x += (this.player.x - this.cam.x) * 0.12;
    this.cam.y += (this.player.y - this.cam.y) * 0.12;
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 40);
  }

  _neighbors(v) {
    const cx = Math.floor(v.x / CELL), cy = Math.floor(v.y / CELL);
    const out = [];
    for (let ix = -1; ix <= 1; ix++) for (let iy = -1; iy <= 1; iy++) {
      const arr = this.grid.get(`${cx + ix},${cy + iy}`);
      if (arr) for (const o of arr) if (!o.dead) out.push(o);
    }
    return out;
  }

  _vehObstacles(v, dt, dtf) {
    const near = this._neighbors(v);
    for (const o of near) {
      const dx = v.x - o.x, dy = v.y - o.y;
      const dist = Math.hypot(dx, dy);
      const minD = v.radius + o.radius;
      if (dist >= minD) continue;
      const spd = Math.abs(v.speed);
      if (v.smashPower >= o.def.tough) {
        if (spd > 0.8) {
          const dmg = spd * v.smashPower * 1.7 * (v.boosting ? 1.5 : 1);
          o.hit(dmg);
          v.speed *= 0.9;
          if (v.isPlayer && o.def.cat !== "small") this.shake = Math.min(16, this.shake + (o.def.cat === "large" ? 12 : 6));
          if (o.dead) this._destroy(v, o);
          else if (v.isPlayer && Math.random() < 0.3) Sound.impact(0.6);
        }
      } else {
        // too tough -> bounce
        const nx = dx / (dist || 1), ny = dy / (dist || 1);
        v.x = o.x + nx * minD; v.y = o.y + ny * minD;
        v.speed *= -0.35;
        o.shake = 0.2;
        if (spd > 1.5) {
          this._spawnParticles(o.x + nx * o.radius, o.y + ny * o.radius, 4, "#FFD166", "spark");
          if (v.isPlayer) { Sound.impact(0.4); this.shake = Math.min(6, this.shake + 3); }
        }
      }
    }
  }

  _destroy(v, o, depth = 0) {
    if (o._destroyed) return;
    o._destroyed = true;
    const mult = v.registerCombo();
    const sc = Math.round(o.def.score * mult);
    v.score += sc; v.objects++;
    const cat = o.def.cat;
    const smashColor = v.isPlayer ? (this.cos.smash || "#C9C9C9") : "#C9C9C9";
    const pc = cat === "large" ? 26 : cat === "medium" ? 14 : 8;
    const cols = DEBRIS[o.def.material] || [o.def.debris];
    cols.forEach((col) => this._spawnParticles(o.x, o.y, Math.ceil(pc / cols.length), col, "debris", o.radius));
    this._spawnParticles(o.x, o.y, Math.floor(pc / 2), smashColor, "dust", o.radius);
    if (o.def.material === "glass") this._spawnParticles(o.x, o.y, cat === "large" ? 14 : 7, "#d6f2ff", "spark", o.radius);
    if (o.def.building) this._spawnParticles(o.x, o.y, 22, o.def.debris, "debris", o.radius * 1.4);
    const arr = this.grid.get(`${o.cx},${o.cy}`);
    if (arr) { const i = arr.indexOf(o); if (i >= 0) arr.splice(i, 1); }
    if (this.multiplayer && v.isPlayer && this.net) this.net.sendDestroy(o);

    if (v.isPlayer) {
      Sound.crash(cat === "large" ? 2 : cat === "medium" ? 1.2 : 0.7);
      this.texts.push(new FloatText(o.x, o.y - o.radius, `+${sc}`, mult >= 3 ? "#FFCC00" : "#FFFFFF", cat === "large" ? 30 : 22));
      if (mult >= 2 && depth === 0) { Sound.combo(v.combo); this.onEvent("combo", { combo: v.combo, mult }); }
      if (depth < 2) {
        const n = cat === "large" ? 7 : cat === "medium" ? 4 : 2;
        const xpEach = Math.ceil((o.def.xp * Math.min(mult, 4)) / n);
        const coinEach = o.def.coins / n;
        for (let i = 0; i < n; i++) this.resources.push(new Resource(o.x, o.y, xpEach, coinEach, 0));
      }
    } else {
      const res = v.addXp(o.def.xp * Math.min(mult, 3));
      if (res.evolved) this._spawnParticles(v.x, v.y, 16, v.color, "spark");
    }
    if (o.def.explosive && depth < 5) this._explode(v, o, depth);
  }

  _explode(v, o, depth) {
    const R = o.def.blast || 100;
    const cat = o.def.cat;
    const smashColor = v.isPlayer ? (this.cos.smash || "#FF6A00") : "#FF6A00";
    this.shockwaves.push({ x: o.x, y: o.y, r: o.radius, maxR: R, life: 0.5, color: smashColor });
    this._spawnParticles(o.x, o.y, cat === "large" ? 30 : 18, smashColor, "spark", R * 0.35);
    this._spawnParticles(o.x, o.y, 10, "#FFCC00", "spark", R * 0.2);
    Sound.crash(cat === "large" ? 2 : 1.5);
    if (v.isPlayer || Math.hypot(o.x - this.player.x, o.y - this.player.y) < 800)
      this.shake = Math.min(24, this.shake + (cat === "large" ? 16 : 9));
    const victims = this._obstaclesInRadius(o.x, o.y, R);
    for (const o2 of victims) {
      if (o2.dead || o2._destroyed) continue;
      if (v.smashPower >= o2.def.tough || o2.def.tough <= 3) {
        o2.dead = true;
        this._destroy(v, o2, depth + 1);
      } else {
        o2.hit(R);
      }
    }
    for (const veh of this.vehicles) {
      if (!veh.alive || veh === v) continue;
      const d = Math.hypot(veh.x - o.x, veh.y - o.y);
      if (d > R || veh.invuln > 0) continue;
      veh.health -= R * 0.6 * (1 - d / R);
      const a = Math.atan2(veh.y - o.y, veh.x - o.x);
      veh.x += Math.cos(a) * 20; veh.y += Math.sin(a) * 20; veh.speed *= 0.5;
      veh.flashTimer = 0.15;
      if (veh.health <= 0) this._killed(veh, v);
    }
  }

  _obstaclesInRadius(x, y, r) {
    const c0x = Math.floor((x - r) / CELL), c1x = Math.floor((x + r) / CELL);
    const c0y = Math.floor((y - r) / CELL), c1y = Math.floor((y + r) / CELL);
    const out = [];
    for (let cx = c0x; cx <= c1x; cx++) for (let cy = c0y; cy <= c1y; cy++) {
      const arr = this.grid.get(`${cx},${cy}`);
      if (!arr) continue;
      for (const o of arr) if (!o.dead && !o._destroyed && Math.hypot(o.x - x, o.y - y) <= r) out.push(o);
    }
    return out;
  }

  _collect(res) {
    const p = this.player;
    if (!p.alive) return;
    p.score += res.score;
    p.matchCoins += res.coins;
    const r = p.addXp(res.xp);
    Sound.pickup();
    this._spawnParticles(res.x, res.y, 3, "#00E5FF", "spark");
    if (r.evolved) this._evolve(r.newTier);
    else if (r.leveled) this._levelUp();
  }

  _levelUp() {
    Sound.levelup();
    this.shake = Math.min(10, this.shake + 6);
    this._spawnParticles(this.player.x, this.player.y, 20, "#FFCC00", "spark");
    this.texts.push(new FloatText(this.player.x, this.player.y - this.player.radius - 20, "LEVEL UP!", "#FFCC00", 28));
    this.onEvent("levelup", { level: this.player.level });
  }
  _evolve(newTier) {
    Sound.evolution();
    this.shake = 20;
    this._spawnParticles(this.player.x, this.player.y, 40, this.player.color, "spark");
    this._spawnParticles(this.player.x, this.player.y, 24, "#FFCC00", "dust");
    this.onEvent("evolution", { level: this.player.level, name: newTier.name, tier: newTier.id });
  }

  _vehCombat(dt, dtf) {
    for (let i = 0; i < this.vehicles.length; i++) {
      const a = this.vehicles[i];
      if (!a.alive) continue;
      for (let j = i + 1; j < this.vehicles.length; j++) {
        const b = this.vehicles[j];
        if (!b.alive) continue;
        const dx = b.x - a.x, dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        const minD = a.radius + b.radius;
        if (dist >= minD || dist === 0) continue;
        const nx = dx / dist, ny = dy / dist;
        const overlap = minD - dist;
        a.x -= nx * overlap * 0.5; a.y -= ny * overlap * 0.5;
        b.x += nx * overlap * 0.5; b.y += ny * overlap * 0.5;
        // damage based on power along impact
        const dmgToB = a.power() * 0.3 * dtf;
        const dmgToA = b.power() * 0.3 * dtf;
        if (b.invuln <= 0) { b.health -= dmgToB; b.flashTimer = 0.15; }
        if (a.invuln <= 0) { a.health -= dmgToA; a.flashTimer = 0.15; }
        a.speed *= 0.6; b.speed *= 0.6;
        a.x -= nx * 2; a.y -= ny * 2; b.x += nx * 2; b.y += ny * 2;
        if ((a.isPlayer || b.isPlayer) && Math.random() < 0.2) { Sound.hit(); this.shake = Math.min(10, this.shake + 4); }
        if (b.health <= 0) this._killed(b, a);
        else if (a.health <= 0) this._killed(a, b);
      }
    }
  }

  _killed(victim, killer) {
    victim.alive = false;
    victim.respawnTimer = victim.isPlayer ? 3 : rand(2, 4);
    killer.kills++;
    const bonus = 500 + victim.level * 120;
    killer.score += bonus;
    const mult = killer.registerCombo();
    this._spawnParticles(victim.x, victim.y, 30, victim.color, "debris", victim.radius);
    this._spawnParticles(victim.x, victim.y, 18, "#FF6A00", "spark");
    if (killer.isPlayer) {
      Sound.crash(2);
      this.texts.push(new FloatText(victim.x, victim.y, `+${bonus} KO!`, "#FF2A55", 30));
      this.onEvent("kill", { name: victim.name });
    }
    if (victim.isPlayer) {
      Sound.death(); this.shake = 18;
      this.onEvent("death", { by: killer.name, respawn: victim.respawnTimer });
    }
  }

  // TEST-ONLY hook: KO the first available rival to prove chained re-kills.
  debugForceKill() {
    const before = this.player.kills;
    if (this.multiplayer) {
      const now = performance.now();
      const g = [...this.ghosts.values()].find((x) => x.koCd <= now);
      if (g) { g.health = 0; this._takedownGhost(g, now); }
    } else {
      const b = this.vehicles.find((v) => !v.isPlayer && v.alive);
      if (b) { b.health = 0; this._killed(b, this.player); }
    }
    return this.player.kills - before;
  }

  // Watch-an-ad revive: freeze the auto-respawn while the ad plays, then bring
  // the player back instantly keeping score & level.
  holdRevive() { if (!this.player.alive) this.awaitingRevive = true; }
  releaseRevive() { this.awaitingRevive = false; } // ad cancelled -> allow normal respawn
  revivePlayer() {
    this.awaitingRevive = false;
    const p = this.player;
    if (p.alive) return;
    p.respawnTimer = 0;
    this._respawn(p);
    p.invuln = 3.5;
    this.shake = Math.min(12, this.shake + 6);
  }

  _respawn(v) {
    v.alive = true;
    v.health = v.maxHealth;
    v.speed = 0;
    v.invuln = 2.5;
    // demote combo but keep level/score
    v.combo = 0;
    const a = rand(0, Math.PI * 2), d = rand(500, 900);
    v.x = Math.max(100, Math.min(WORLD.w - 100, WORLD.w / 2 + Math.cos(a) * d));
    v.y = Math.max(100, Math.min(WORLD.h - 100, WORLD.h / 2 + Math.sin(a) * d));
    if (v.isPlayer) this.onEvent("respawn", {});
  }

  _spawnParticles(x, y, n, color, type, spread = 20) {
    const cap = this.quality === "low" ? 260 : 520;
    if (this.particles.length > cap) return;
    for (let i = 0; i < n; i++) {
      const a = rand(0, Math.PI * 2);
      const s = type === "spark" ? rand(2, 7) : rand(1, 4);
      const life = type === "dust" ? rand(0.4, 0.9) : rand(0.4, 1.1);
      const size = type === "debris" ? rand(3, 8) : type === "dust" ? rand(6, 14) : rand(2, 4);
      this.particles.push(new Particle(
        x + rand(-spread / 3, spread / 3), y + rand(-spread / 3, spread / 3),
        Math.cos(a) * s, Math.sin(a) * s, life, color, size, type
      ));
    }
  }

  _rank() {
    if (this.multiplayer) {
      const self = this.net && this.net.pid;
      const idx = this.mpStandings.findIndex((r) => r.pid === self);
      return { rank: idx >= 0 ? idx + 1 : (this.mpTotal || 1), total: this.mpTotal || this.mpStandings.length, sorted: this.mpStandings };
    }
    const sorted = [...this.vehicles].sort((a, b) => b.score - a.score);
    return { rank: sorted.indexOf(this.player) + 1, total: this.vehicles.length, sorted };
  }

  // ---------------- MULTIPLAYER ----------------
  _makeGhost(e) {
    const mt = VEHICLES.length - 1;
    const hp = VEHICLES[Math.min(e.tier || 0, mt)].maxHealth * 0.42;
    return {
      pid: e.pid, x: e.x, y: e.y, tx: e.x, ty: e.y, angle: e.angle || 0, ta: e.angle || 0,
      tier: e.tier || 0, radius: VEHICLES[Math.min(e.tier || 0, mt)].radius,
      color: e.color, trailColor: e.color, name: e.name, score: e.score || 0,
      health: hp, maxHealth: hp, koCd: 0, combo: 0, comboTimer: 0, invuln: 0, flashTimer: 0,
      boosting: !!e.boosting, trail: [], alive: true, isPlayer: false, bot: !!e.bot,
    };
  }
  applySnapshot(m) {
    this.timeLeft = m.timeLeft;
    const self = this.net && this.net.pid;
    const mt = VEHICLES.length - 1;
    const seen = new Set();
    const upsert = (e) => {
      if (e.pid === self) return;
      seen.add(e.pid);
      let g = this.ghosts.get(e.pid);
      if (!g) { g = this._makeGhost(e); this.ghosts.set(e.pid, g); }
      g.tx = e.x; g.ty = e.y; g.ta = e.angle || 0;
      g.tier = e.tier || 0; g.radius = VEHICLES[Math.min(e.tier || 0, mt)].radius;
      const nm = VEHICLES[Math.min(e.tier || 0, mt)].maxHealth * 0.42;
      if (g.maxHealth !== nm) { const ratio = g.health / g.maxHealth; g.maxHealth = nm; g.health = nm * Math.max(0, Math.min(1, ratio)); }
      g.score = e.score || 0; g.name = e.name; g.color = e.color; g.trailColor = e.color;
      g.boosting = !!e.boosting; g.bot = !!e.bot;
    };
    for (const e of m.players) upsert(e);
    for (const e of m.bots) upsert(e);
    for (const pid of [...this.ghosts.keys()]) if (!seen.has(pid)) this.ghosts.delete(pid);
    const rows = [
      ...m.players.filter((p) => p.pid !== self).map((p) => ({ pid: p.pid, name: p.name, score: p.score, color: p.color })),
      ...m.bots.map((b) => ({ pid: b.pid, name: b.name, score: b.score, color: b.color })),
      { pid: self, name: this.player.name, score: this.player.score, color: this.player.color, isPlayer: true },
    ];
    rows.sort((a, b) => b.score - a.score);
    this.mpStandings = rows; this.mpTotal = rows.length;
    this.leaderPid = rows.length ? rows[0].pid : null;
  }
  applyRemoteDestroy(m) {
    const near = this._obstaclesInRadius(m.x, m.y, 75);
    let best = null, bd = 1e9;
    for (const o of near) {
      if (o.dead || o._destroyed) continue;
      if (m.key && o.key !== m.key) continue;
      const d = Math.hypot(o.x - m.x, o.y - m.y);
      if (d < bd) { bd = d; best = o; }
    }
    if (!best) return;
    best._destroyed = true; best.dead = true;
    const arr = this.grid.get(`${best.cx},${best.cy}`);
    if (arr) { const i = arr.indexOf(best); if (i >= 0) arr.splice(i, 1); }
    const cat = best.def.cat;
    const cols = DEBRIS[best.def.material] || [best.def.debris];
    cols.forEach((col) => this._spawnParticles(best.x, best.y, cat === "large" ? 8 : 4, col, "debris", best.radius));
    if (best.def.explosive) this.shockwaves.push({ x: best.x, y: best.y, r: best.radius, maxR: best.def.blast || 100, life: 0.5, color: "#FF6A00" });
  }
  _isLeader(v) {
    if (this.multiplayer) {
      if (!this.leaderPid) return false;
      const self = this.net && this.net.pid;
      return v.isPlayer ? this.leaderPid === self : this.leaderPid === v.pid;
    }
    return v === this.leaderRef;
  }
  _ghostCombat() {
    const p = this.player;
    if (!p.alive) return;
    const now = performance.now();
    const selfLeader = this._isLeader(p);
    for (const g of this.ghosts.values()) {
      const dx = g.x - p.x, dy = g.y - p.y;
      const dist = Math.hypot(dx, dy);
      const minD = p.radius + g.radius;
      if (dist >= minD || dist === 0) continue;
      const nx = dx / dist, ny = dy / dist, overlap = minD - dist;
      p.x -= nx * overlap; p.y -= ny * overlap;
      const spd = Math.abs(p.speed);
      // Online takedown: ram a rival to damage it. You always land a hit while
      // moving (past a short KO cooldown) — bigger tier & boost hit far harder,
      // smaller tier still chips away, so rivals stay killable a 2nd, 3rd time
      // even after they've out-levelled you. The score LEADER is a bounty target.
      if (g.koCd <= now && spd > 0.8) {
        const tierDiff = p.tier - g.tier; // + you're bigger, - you're smaller
        const tierMult = tierDiff >= 0 ? 1 + tierDiff * 0.25 : Math.max(0.6, 1 + tierDiff * 0.18);
        const bounty = this._isLeader(g) ? 1.7 : 1;
        const dmg = Math.max(8, p.power() * 1.15 * tierMult * (p.boosting ? 1.5 : 1) * bounty);
        g.health -= dmg; g.flashTimer = 0.15; p.speed *= 0.8;
        this._spawnParticles((p.x + g.x) / 2, (p.y + g.y) / 2, 4, this.cos.smash || "#FF6A00", "spark");
        if (Math.random() < 0.25) { Sound.hit(); this.shake = Math.min(9, this.shake + 3); }
        // Ramming a stronger enemy still shoves you and can hurt you back.
        if (g.bot && p.invuln <= 0 && tierDiff < 0) {
          this._playerHitByBot(g, (4 + g.tier * 0.7) * (g.boosting ? 1.3 : 1));
        }
        if (g.health <= 0) this._takedownGhost(g, now);
      } else {
        p.speed *= 0.7; p.x -= nx * 3; p.y -= ny * 3;
        // Bots can wreck YOU when they out-tier you or when you're the leader
        // (real rivals handle this on their own client via the kill relay).
        if (g.bot && p.invuln <= 0 && (g.tier > p.tier || selfLeader || g.boosting)) {
          const base = 6 + g.tier * 0.9;
          this._playerHitByBot(g, base * (selfLeader ? 1.8 : 1) * (g.boosting ? 1.3 : 1));
        }
      }
    }
  }
  _playerHitByBot(g, dmg) {
    const p = this.player;
    if (!p.alive || p.invuln > 0) return;
    p.health -= dmg; p.flashTimer = 0.15;
    if (Math.random() < 0.2) { Sound.hit(); this.shake = Math.min(11, this.shake + 4); }
    if (p.health <= 0) {
      p.alive = false; p.respawnTimer = 3;
      p.score = Math.max(0, Math.floor(p.score * 0.9)); // taken down: lose some of your lead
      Sound.death(); this.shake = 16;
      this._spawnParticles(p.x, p.y, 24, p.color, "debris", p.radius);
      this.onEvent("death", { by: g.name, respawn: 3 });
    }
  }
  _takedownGhost(g, now) {
    const p = this.player;
    p.kills++;
    const dethrone = this._isLeader(g);
    let bonus = 500 + g.tier * 150;
    if (dethrone) {
      bonus += 1500; p.xp += 300; p.matchCoins = (p.matchCoins || 0) + 120;
      this.texts.push(new FloatText(g.x, g.y - 26, "DETHRONED!", "#FFD700", 34));
      this._spawnParticles(g.x, g.y, 26, "#FFD700", "spark");
      Sound.victory && Sound.victory();
    }
    p.score += bonus;
    p.registerCombo();
    this._spawnParticles(g.x, g.y, 30, g.color, "debris", g.radius);
    this._spawnParticles(g.x, g.y, 18, "#FF6A00", "spark");
    this.texts.push(new FloatText(g.x, g.y, `+${bonus} KO!`, dethrone ? "#FFD700" : "#FF2A55", 30));
    Sound.crash(2);
    this.onEvent("kill", { name: g.name });
    // Rival is knocked out, then quickly re-enters weakened (short KO window +
    // partial health) so you can reliably chain a 2nd, 3rd, 4th takedown.
    g.health = g.maxHealth * 0.55; g.koCd = now + 700;
    // Real players are told they were wrecked; server relays it to their client.
    if (!g.bot && this.net) this.net.sendKill(g.pid);
  }
  _remoteKilledMe(by) {
    const p = this.player;
    if (!p.alive) return;
    p.alive = false; p.respawnTimer = 3;
    Sound.death(); this.shake = 16;
    this._spawnParticles(p.x, p.y, 24, p.color, "debris", p.radius);
    this.onEvent("death", { by: by || "a rival", respawn: 3 });
  }
  _endMatchMP(standings) {
    if (this.mpEnded) return;
    this.mpEnded = true; this.ended = true;
    const self = this.net && this.net.pid;
    const idx = (standings || []).findIndex((r) => r.pid === self);
    const rank = idx >= 0 ? idx + 1 : (standings ? standings.length : 1);
    const p = this.player;
    if (rank === 1) Sound.victory();
    this.onEvent("matchend", {
      rank, score: p.score, objectsSmashed: p.objects, kills: p.kills,
      maxCombo: p.maxCombo, xpEarned: p.xp, coinsEarned: Math.round(p.matchCoins + p.score / 40),
      durationMs: this.matchDuration * 1000,
    });
  }
  // Seamlessly continue as a local match if the live connection drops mid-game.
  goOffline() {
    if (this.ended || !this.multiplayer) return;
    this.multiplayer = false;
    this.net = null;
    this.ghosts.clear();
    if (!this.timeLeft || this.timeLeft <= 0) this.timeLeft = 60;
  }
  // Current progress as a submittable result — so leaving early still banks coins/XP.
  snapshotResult() {
    const p = this.player;
    let rank = 1;
    if (this.multiplayer) {
      const self = this.net && this.net.pid;
      const i = this.mpStandings.findIndex((r) => r.pid === self);
      rank = i >= 0 ? i + 1 : (this.mpTotal || 1);
    } else {
      rank = this._rank().rank;
    }
    const elapsed = Math.max(1, (this.matchDuration || 210) - (this.timeLeft || 0));
    return {
      score: p.score, objectsSmashed: p.objects, kills: p.kills, maxCombo: p.maxCombo,
      xpEarned: p.xp, coinsEarned: Math.round((p.matchCoins || 0) + p.score / 40),
      rank, durationMs: Math.round(elapsed * 1000),
    };
  }

  _pushHud() {
    const p = this.player;
    const { rank, total, sorted } = this._rank();
    const xpInto = p.xp - xpNeed(p.level);
    const xpNext = xpNeed(p.level + 1) - xpNeed(p.level);
    const lb = sorted.slice(0, 5).map((v) => ({ name: v.name, score: v.score, isPlayer: v.isPlayer || v === p, level: v.level, color: v.color }));
    if (!lb.some((e) => e.isPlayer)) lb.push({ name: p.name, score: p.score, isPlayer: true, level: p.level, color: p.color, rank });
    const ents = this.multiplayer
      ? [{ x: p.x, y: p.y, isPlayer: true, color: p.color, r: p.radius }, ...[...this.ghosts.values()].map((g) => ({ x: g.x, y: g.y, isPlayer: false, color: g.color, r: g.radius }))]
      : this.vehicles.filter((v) => v.alive).map((v) => ({ x: v.x, y: v.y, isPlayer: v.isPlayer, color: v.color, r: v.radius }));
    this.onHud({
      level: p.level, xpInto, xpNext, xpPct: Math.max(0, Math.min(1, xpInto / xpNext)),
      score: p.score, health: Math.max(0, p.health), maxHealth: p.maxHealth,
      boost: p.boostEnergy, boostMax: p.boostMax, boosting: p.boosting,
      combo: p.combo, comboMult: comboMult(p.combo),
      rank, total, timeLeft: Math.ceil(this.timeLeft),
      tierName: p.tierDef().name, alive: p.alive, respawnIn: Math.ceil(p.respawnTimer),
      objects: p.objects, kills: p.kills, multiplayer: this.multiplayer,
      isLeader: this._isLeader(p),
      leaderboard: lb,
      minimap: { w: WORLD.w, h: WORLD.h, ents },
    });
  }

  _endMatch() {
    if (this.ended) return;
    this.ended = true;
    const { rank } = this._rank();
    const p = this.player;
    if (rank === 1) Sound.victory();
    this.onEvent("matchend", {
      rank, score: p.score, objectsSmashed: p.objects, kills: p.kills,
      maxCombo: p.maxCombo, xpEarned: p.xp, coinsEarned: Math.round(p.matchCoins + p.score / 40),
      durationMs: this.matchDuration * 1000,
    });
  }

  // ---------------- RENDER ----------------
  _render() {
    const ctx = this.ctx, cv = this.canvas;
    const w = cv.width, h = cv.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#0a0e18";
    ctx.fillRect(0, 0, w, h);
    const z = this.cam.zoom;
    const sx = this.shake ? rand(-this.shake, this.shake) : 0;
    const sy = this.shake ? rand(-this.shake, this.shake) : 0;
    ctx.save();
    ctx.translate(w / 2 + sx, h / 2 + sy);
    ctx.scale(z, z);
    ctx.translate(-this.cam.x, -this.cam.y);

    // view bounds for culling
    const vb = { x1: this.cam.x - w / 2 / z - 80, y1: this.cam.y - h / 2 / z - 80, x2: this.cam.x + w / 2 / z + 80, y2: this.cam.y + h / 2 / z + 80 };

    this._drawGround(ctx, vb);

    // resources
    for (const r of this.resources) {
      if (r.x < vb.x1 || r.x > vb.x2 || r.y < vb.y1 || r.y > vb.y2) continue;
      ctx.beginPath(); ctx.arc(r.x, r.y, 6, 0, 6.28);
      ctx.fillStyle = "#00E5FF"; ctx.shadowColor = "#00E5FF"; ctx.shadowBlur = 12;
      ctx.fill(); ctx.shadowBlur = 0;
    }

    // obstacles
    for (const o of this.obstacles) {
      if (o.dead) continue;
      if (o.x < vb.x1 || o.x > vb.x2 || o.y < vb.y1 || o.y > vb.y2) continue;
      this._drawObstacle(ctx, o);
    }

    // particles (behind vehicles)
    for (const p of this.particles) {
      const alpha = Math.max(0, p.life / p.max);
      ctx.globalAlpha = alpha;
      if (p.type === "dust") {
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size * (2 - alpha), 0, 6.28); ctx.fill();
      } else if (p.type === "debris") {
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color; ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size); ctx.restore();
      } else {
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, 6.28); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    // shockwaves
    for (const s of this.shockwaves) {
      ctx.globalAlpha = Math.max(0, s.life * 1.8);
      ctx.strokeStyle = s.color; ctx.lineWidth = 5;
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, 6.28); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // vehicles
    for (const v of this.vehicles) if (v.alive) this._drawVehicle(ctx, v);
    if (this.multiplayer) for (const g of this.ghosts.values()) this._drawVehicle(ctx, g);

    // floating text
    ctx.textAlign = "center";
    for (const t of this.texts) {
      ctx.globalAlpha = Math.max(0, t.life);
      ctx.font = `900 ${t.size}px Outfit, sans-serif`;
      ctx.fillStyle = t.color; ctx.strokeStyle = "rgba(0,0,0,0.6)"; ctx.lineWidth = 4;
      ctx.strokeText(t.text, t.x, t.y); ctx.fillText(t.text, t.x, t.y);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  _drawGround(ctx, vb) {
    // zone tints
    for (const zn of ZONES) {
      ctx.fillStyle = zn.tint;
      ctx.fillRect(zn.x, zn.y, zn.w, zn.h);
    }
    // grid roads
    ctx.strokeStyle = "rgba(255,255,255,0.045)"; ctx.lineWidth = 2;
    const step = 300;
    const startX = Math.max(0, Math.floor(vb.x1 / step) * step);
    const startY = Math.max(0, Math.floor(vb.y1 / step) * step);
    ctx.beginPath();
    for (let x = startX; x < Math.min(WORLD.w, vb.x2); x += step) { ctx.moveTo(x, Math.max(0, vb.y1)); ctx.lineTo(x, Math.min(WORLD.h, vb.y2)); }
    for (let y = startY; y < Math.min(WORLD.h, vb.y2); y += step) { ctx.moveTo(Math.max(0, vb.x1), y); ctx.lineTo(Math.min(WORLD.w, vb.x2), y); }
    ctx.stroke();
    // world border
    ctx.strokeStyle = "rgba(255,149,0,0.5)"; ctx.lineWidth = 12;
    ctx.strokeRect(0, 0, WORLD.w, WORLD.h);
  }

  _drawObstacle(ctx, o) {
    ctx.save();
    let ox = 0, oy = 0;
    if (o.shake > 0) { ox = rand(-2, 2); oy = rand(-2, 2); o.shake -= 0.02; }
    ctx.translate(o.x + ox, o.y + oy);
    ctx.rotate(o.angle);
    if (o.def.explosive) {
      ctx.save();
      const pulse = 0.4 + 0.3 * Math.sin(performance.now() / 260 + o.x);
      ctx.shadowColor = o.key === "barrel" ? "#FF6A00" : "rgba(255,120,40,0.9)";
      ctx.shadowBlur = o.radius * pulse;
      paintObstacle(ctx, o);
      ctx.restore();
    } else {
      paintObstacle(ctx, o);
    }
    ctx.restore();
  }

  _drawVehicle(ctx, v) {
    // trail (uses equipped trail cosmetic; glows + fattens while boosting)
    if (v.trail.length > 1) {
      const boosting = v.boosting;
      ctx.save();
      ctx.shadowColor = v.trailColor; ctx.shadowBlur = boosting ? 16 : 6;
      for (let i = 0; i < v.trail.length; i++) {
        const p = v.trail[i];
        ctx.globalAlpha = (p.life / 0.4) * (boosting ? 0.72 : 0.5);
        ctx.fillStyle = v.trailColor;
        ctx.beginPath(); ctx.arc(p.x, p.y, v.radius * (boosting ? 0.78 : 0.6) * (i / v.trail.length), 0, 6.28); ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }
    ctx.save();
    ctx.translate(v.x, v.y);
    ctx.rotate(v.angle);
    const r = v.radius;
    const flash = v.flashTimer > 0 && Math.floor(v.flashTimer * 30) % 2 === 0;
    if (v.invuln > 0) ctx.globalAlpha = 0.55 + 0.45 * Math.sin(performance.now() / 80);
    // boost jet flame out the back — coloured by the equipped boost cosmetic
    if (v.boosting) {
      const bc = v.isPlayer ? this._boostColor : v.color;
      const flick = 0.8 + Math.random() * 0.45;
      ctx.save();
      ctx.shadowColor = bc; ctx.shadowBlur = r * 0.9;
      ctx.fillStyle = bc; ctx.globalAlpha = 0.9;
      ctx.beginPath();
      ctx.moveTo(-r * 0.9, -r * 0.42);
      ctx.quadraticCurveTo(-r * 1.7 * flick, 0, -r * 0.9, r * 0.42);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#FFF3B0"; ctx.globalAlpha = 0.85;
      ctx.beginPath();
      ctx.moveTo(-r * 0.9, -r * 0.22);
      ctx.quadraticCurveTo(-r * 1.25 * flick, 0, -r * 0.9, r * 0.22);
      ctx.closePath(); ctx.fill();
      ctx.restore();
    }
    paintVehicle(ctx, {
      tier: v.tier, color: v.color, radius: r, flash,
      theme: v.theme || themeFromColor(v.color),
      wheelColor: v.isPlayer ? (this.cos.wheel || "#1a1a1a") : "#1a1a1a",
    });
    ctx.globalAlpha = 1;
    ctx.restore();

    // health bar + name
    const barW = r * 2;
    const hp = Math.max(0, v.health / v.maxHealth);
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(v.x - barW / 2, v.y - r - 16, barW, 6);
    ctx.fillStyle = hp > 0.5 ? "#10B981" : hp > 0.25 ? "#FFCC00" : "#FF2A55";
    ctx.fillRect(v.x - barW / 2, v.y - r - 16, barW * hp, 6);
    ctx.textAlign = "center";
    ctx.font = `800 ${Math.max(12, r * 0.4)}px Outfit, sans-serif`;
    ctx.fillStyle = v.isPlayer ? "#FF9500" : "#cbd5e1";
    ctx.strokeStyle = "rgba(0,0,0,0.7)"; ctx.lineWidth = 3;
    ctx.strokeText(v.name, v.x, v.y - r - 22);
    ctx.fillText(v.name, v.x, v.y - r - 22);
    // crown on the current score leader
    if (this._isLeader(v)) this._drawCrown(ctx, v.x, v.y - r - (v.name ? 40 : 30), r);
    // player combo ring
    if (v.isPlayer && v.combo >= 2) {
      ctx.beginPath(); ctx.arc(v.x, v.y, r + 8, -Math.PI / 2, -Math.PI / 2 + (v.comboTimer / 3.6) * 6.28);
      ctx.strokeStyle = "#FFCC00"; ctx.lineWidth = 3; ctx.stroke();
    }
  }
  _drawCrown(ctx, x, y, r) {
    const w = Math.max(16, r * 0.7), h = w * 0.6;
    ctx.save();
    ctx.translate(x, y);
    const bob = Math.sin(performance.now() / 300) * 2;
    ctx.translate(0, bob);
    ctx.shadowColor = "#FFD700"; ctx.shadowBlur = 12;
    ctx.fillStyle = "#FFD700"; ctx.strokeStyle = "#b8860b"; ctx.lineWidth = Math.max(1.5, w * 0.06);
    ctx.beginPath();
    ctx.moveTo(-w / 2, h / 2);
    ctx.lineTo(-w / 2, -h * 0.2);
    ctx.lineTo(-w / 4, h * 0.1);
    ctx.lineTo(0, -h / 2);
    ctx.lineTo(w / 4, h * 0.1);
    ctx.lineTo(w / 2, -h * 0.2);
    ctx.lineTo(w / 2, h / 2);
    ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#FF3B30";
    ctx.beginPath(); ctx.arc(0, h * 0.18, w * 0.09, 0, 6.28); ctx.fill();
    ctx.restore();
  }
}

function xpNeed(level) {
  if (level <= 1) return 0;
  return Math.floor(60 * (level - 1) + 8 * Math.pow(level - 1, 2.15));
}
const COLOR_THEME = { "#FF9500": "classic", "#FF3B30": "fire", "#00E5FF": "neon", "#C0C6D0": "chrome", "#D9A05B": "desert", "#9D4EDD": "cyber", "#39FF14": "neon" };
function themeFromColor(c) { return COLOR_THEME[(c || "").toUpperCase()] || "classic"; }
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
